import { useFeatureFlagEnabled } from "@posthog/react";
import { MARKETPLACE_FLAG_KEY } from "../lib/feature-flags";
import { useMarketplaceGateBase } from "./useMarketplaceGate";

/**
 * Resolves whether the Pro CRM Marketplace surface (the "Marketplace" header
 * entry, the pageSidebar mini-app, every `/marketplace/*` route, and the
 * connection-fee rate card on the marketplace Profile page) is enabled for the
 * current pro.
 *
 * Fail-closed in prod: `useFeatureFlagEnabled` returns `true | false |
 * undefined`, and only an explicit `true` unlocks marketplace — an absent /
 * still-loading / disabled flag keeps every marketplace surface hidden, so a
 * controlled PostHog rollout governs exposure (and the flag doubles as a
 * kill-switch). `import.meta.env.DEV` is OR-ed in so local dev + preview builds
 * — which usually have no PostHog project wired and would otherwise see
 * `undefined` — keep marketplace usable without a flag. Mirrors
 * `useCopilotEnabled` exactly.
 *
 * Reuses the consumer `marketplace_go_live_enabled` flag (see
 * MARKETPLACE_FLAG_KEY) so one switch controls buyer- and agent-facing
 * marketplace together.
 */
export function useMarketplaceEnabled(): boolean {
  // Loan officers also need `lender_marketplace_enabled` (agents are `on`).
  return useMarketplaceGateBase() === "on";
}

/**
 * Whether the `/marketplace/*` routes are REGISTERED. Unlike
 * `useMarketplaceEnabled`, a loan officer whose lender gate is still
 * `loading` (role not yet resolved: the profile store is filled by
 * `ProAuthGate`, which only runs inside an already-matched route) keeps the
 * routes registered, so a cold-load deep link is not lost to the catch-all.
 * `MarketplaceLayout` holds the page back until the gate resolves.
 */
export function useMarketplaceRoutesRegistered(): boolean {
  const flag = useFeatureFlagEnabled(MARKETPLACE_FLAG_KEY);
  const gate = useMarketplaceGateBase();
  // `loading` with the flag already true means only the lender role is
  // unresolved; a still-loading flag keeps the routes unregistered.
  return gate === "on" || (gate === "loading" && flag === true);
}
